import { esquemaLogin, MOTIVO_RECHAZO } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { authClient, queryClient, useTRPC } from "../api.ts";
import { Barras, Logo } from "../componentes/Barras.tsx";
import { Aviso, Campo, CLASE_ENTRADA } from "../componentes/ui.tsx";

/** Lo que se le dice a quien la API devolvió a /login sin acceso externo (`MOTIVO_RECHAZO`). */
const SIN_ACCESO_EXTERNO =
  "Tu cuenta no tiene habilitado el acceso desde fuera de la red. Entra desde la oficina o pídele a un administrador que lo habilite.";
/** Y a quien un administrador desactivó. */
const USUARIO_DESACTIVADO =
  "Tu usuario está desactivado. Si crees que es un error, habla con un administrador.";

/**
 * Lo que better-auth pone en ?error= al volver de Google sin sesión. "signup_disabled": la
 * cuenta de Google no corresponde a nadie dado de alta (Google no da de alta a nadie).
 * "sin_acceso_externo" lo pone la API (fase 6): credenciales bien, pero desde fuera no entra.
 */
function mensajeDeGoogle(codigo: string): string {
  if (codigo === MOTIVO_RECHAZO.sinAccesoExterno) return SIN_ACCESO_EXTERNO;
  if (codigo === MOTIVO_RECHAZO.usuarioDesactivado) return USUARIO_DESACTIVADO;
  // El "state" vive en una cookie del host donde se tocó el botón: falta si se volvió a otra
  // dirección (localhost ↔ la IP) o se abrió la dirección de regreso a mano.
  if (/state|restart/i.test(codigo)) {
    return "El inicio con Google no se completó. Toca de nuevo «Entrar con Google» desde esta misma página.";
  }
  if (/signup|user_not_found|not_found/i.test(codigo)) {
    return "Esa cuenta de Google no está dada de alta en GestionDOC. Pídele a un administrador que te registre con ese correo.";
  }
  if (/hd|domain|dominio/i.test(codigo)) {
    return "Entra con tu cuenta de Google de SMG Comercial.";
  }
  return "No se pudo entrar con Google. Prueba de nuevo o entra con tu contraseña.";
}

export function PaginaLogin() {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const search = useSearch({ from: "/login" });
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const metodos = useQuery({ ...trpc.metodosDeEntrada.queryOptions(), staleTime: Infinity });
  const destino = search.volverA && search.volverA !== "/login" ? search.volverA : "/";

  // El ?error= de la vuelta de Google se muestra una vez y se quita de la dirección (si no, al
  // recargar vuelve a salir). Y solo si Google está activo: si no, el aviso pide tocar un botón
  // que no aparece.
  const errorDeGoogle = search.error;
  useEffect(() => {
    if (!errorDeGoogle || !metodos.isSuccess) return;
    if (
      metodos.data.google ||
      errorDeGoogle === MOTIVO_RECHAZO.sinAccesoExterno ||
      errorDeGoogle === MOTIVO_RECHAZO.usuarioDesactivado
    ) {
      setError(mensajeDeGoogle(errorDeGoogle));
    }
    navigate({
      to: "/login",
      search: search.volverA ? { volverA: search.volverA } : {},
      replace: true,
    });
  }, [errorDeGoogle, metodos.isSuccess, metodos.data, navigate, search.volverA]);

  async function conGoogle() {
    setError(null);
    setEnviando(true);
    const r = await authClient.signIn.social({
      provider: "google",
      callbackURL: destino,
      errorCallbackURL: "/login",
    });
    // Si sale bien, el navegador ya se fue a Google; solo se vuelve aquí si no se pudo empezar.
    if (r.error) {
      setEnviando(false);
      setError(mensajeDeGoogle(r.error.message ?? ""));
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const datos = esquemaLogin.safeParse({ correo, contrasena });
    if (!datos.success) {
      setError(datos.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    setEnviando(true);
    const r = await authClient.signIn.email({
      email: datos.data.correo,
      password: datos.data.contrasena,
    });
    setEnviando(false);
    if (r.error) {
      setError(
        r.error.status === 401 || r.error.status === 400
          ? "Correo o contraseña incorrectos"
          : (r.error.message ?? "No se pudo iniciar sesión"),
      );
      return;
    }
    queryClient.clear();
    navigate({ to: destino });
  }

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-marino-profundo p-6">
      {/*
        La planta de SMG de fondo, a pantalla completa y bajo un velo marino para que el formulario
        se lea. Se ve entera, sin acercar. El archivo ya no trae el logo que la foto original tenía
        abajo a la derecha (se tapó con el piso de concreto): el logo de la pantalla es el de arriba
        del formulario. El 3 % de aumento solo esconde el filo blanco del borde de la foto.
      */}
      <img
        src="/marca/planta-aerea.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 size-full scale-[1.03] object-cover object-center"
      />
      <div className="absolute inset-0 bg-marino-profundo/75" />
      {/*
        Las tres barras del logo, grandes, encima de la foto: rojas abajo a la derecha y blancas
        arriba a la izquierda. Van más marcadas que sobre el fondo liso, para que se lean sobre
        la imagen.
      */}
      <div className="pointer-events-none absolute -right-24 -bottom-16 opacity-[.55] select-none">
        <Barras alto={520} color="#c8352e" />
      </div>
      <div className="pointer-events-none absolute -top-10 -left-20 opacity-[.22] select-none">
        <Barras alto={260} color="#ffffff" />
      </div>

      <div className="relative w-full max-w-[400px]">
        <div className="mb-5 flex justify-center">
          <Logo variante="blanco" alto={56} />
        </div>

        <form
          onSubmit={enviar}
          className="rounded-lamina bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,.35)]"
        >
          <div className="mb-6">
            <div className="mb-2 text-rojo-marca">
              <Barras alto={12} />
            </div>
            <h1 className="text-[20px] font-bold text-marino">Gestión Documental</h1>
            <div className="text-[12.5px] text-texto-3">
              Sistema de gestión de la calidad · SMG Comercial
            </div>
          </div>

          <Campo etiqueta="Correo" className="mb-3">
            <input
              type="email"
              autoComplete="username"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className={ENTRADA_LOGIN}
              placeholder="nombre.apellido@smgcomercialhn.com"
            />
          </Campo>
          <Campo etiqueta="Contraseña" className="mb-5">
            <input
              type="password"
              autoComplete="current-password"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              className={ENTRADA_LOGIN}
            />
          </Campo>

          {error && <Aviso className="mb-4">{error}</Aviso>}

          <button
            type="submit"
            disabled={enviando}
            className="w-full rounded-md bg-marino py-2.5 text-[13px] font-semibold text-white hover:bg-marino-profundo disabled:opacity-60"
          >
            {enviando ? "Entrando…" : "Iniciar sesión"}
          </button>

          {metodos.data?.google && (
            <>
              <div className="my-4 flex items-center gap-3 text-[11px] text-texto-3">
                <span className="h-px flex-1 bg-borde" />o<span className="h-px flex-1 bg-borde" />
              </div>
              <button
                type="button"
                onClick={() => void conGoogle()}
                disabled={enviando}
                className="flex w-full items-center justify-center gap-2.5 rounded-md border border-borde bg-white py-2.5 text-[13px] font-semibold text-texto hover:bg-fondo disabled:opacity-60"
              >
                <LogoGoogle />
                Entrar con Google
              </button>
            </>
          )}

          <p className="mt-5 text-center text-[11px] text-texto-3">
            Acceso solo para personal autorizado. Cada inicio de sesión queda registrado.
          </p>
        </form>

        <p className="mt-5 text-center font-display text-[11px] font-semibold tracking-[.18em] text-white/50 uppercase">
          Creamos sabores y soluciones
        </p>
      </div>
    </div>
  );
}

/** Los campos del login van un punto más grandes que los del resto de la app. */
const ENTRADA_LOGIN = `${CLASE_ENTRADA} !text-[14px]`;

/** La "G" de Google, en sus cuatro colores (marca de Google para los botones de acceso). */
function LogoGoogle() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
